// src/components/MobileDealCards.jsx
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Stack,
  CircularProgress,
  alpha,
  useTheme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

const MobileDealCards = ({ deals, onEdit, onClone, onLoad, loading = false }) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (deals.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
          No deals found
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 3,
        width: '100%'
      }}>
        {deals.map((deal, index) => (
          <Card
            key={index}
            elevation={8}
            sx={{
              borderRadius: 4,
              border: `4px solid ${theme.palette.primary.main}`,
              bgcolor: theme.palette.background.paper,
              mb: 3, // Explicit margin bottom
              minHeight: 300, // Ensure cards have substantial height
              boxShadow: '0 8px 20px rgba(0, 0, 0, 0.3)',
              transition: 'all 0.2s ease',
              width: '100%'
            }}
          >
            <CardContent sx={{ p: 4, '&:last-child': { pb: 4 } }}>
              {/* Deal Name */}
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  fontSize: '20px',
                  mb: 2,
                  color: theme.palette.primary.main,
                  wordBreak: 'break-word',
                  borderBottom: `2px solid ${theme.palette.divider}`,
                  pb: 1
                }}
              >
                {deal['Deal Name'] || 'Unnamed Deal'}
              </Typography>

              {/* Key Information */}
              <Stack spacing={1.5} sx={{ mb: 2.5 }}>
                {deal['Type'] && (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    bgcolor: alpha(theme.palette.background.default, 0.5),
                    p: 1.5,
                    borderRadius: 1
                  }}>
                    <Typography variant="body1" sx={{ color: theme.palette.text.secondary, fontSize: '15px', fontWeight: 600 }}>
                      Type:
                    </Typography>
                    <Chip
                      label={deal['Type']}
                      sx={{
                        height: 32,
                        fontSize: '14px',
                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                        color: theme.palette.primary.main,
                        fontWeight: 700
                      }}
                    />
                  </Box>
                )}

                {deal['Security Owner'] && (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    bgcolor: alpha(theme.palette.background.default, 0.5),
                    p: 1.5,
                    borderRadius: 1
                  }}>
                    <Typography variant="body1" sx={{ color: theme.palette.text.secondary, fontSize: '15px', fontWeight: 600 }}>
                      Owner:
                    </Typography>
                    <Typography variant="body1" sx={{ color: theme.palette.text.primary, fontSize: '15px', fontWeight: 600 }}>
                      {deal['Security Owner']}
                    </Typography>
                  </Box>
                )}

                {deal['Tranche'] && (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    bgcolor: alpha(theme.palette.background.default, 0.5),
                    p: 1.5,
                    borderRadius: 1
                  }}>
                    <Typography variant="body1" sx={{ color: theme.palette.text.secondary, fontSize: '15px', fontWeight: 600 }}>
                      Tranches:
                    </Typography>
                    <Typography variant="body1" sx={{ color: theme.palette.text.primary, fontSize: '15px', fontWeight: 600 }}>
                      {deal['Tranche']}
                    </Typography>
                  </Box>
                )}

                {deal['CUSIP'] && (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    bgcolor: alpha(theme.palette.background.default, 0.5),
                    p: 1.5,
                    borderRadius: 1,
                    gap: 2
                  }}>
                    <Typography variant="body1" sx={{ color: theme.palette.text.secondary, fontSize: '15px', fontWeight: 600, flexShrink: 0 }}>
                      CUSIP:
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: theme.palette.text.primary, 
                        fontSize: '14px', 
                        fontFamily: 'monospace',
                        fontWeight: 600,
                        textAlign: 'right',
                        wordBreak: 'break-all',
                        flex: 1
                      }}
                    >
                      {deal['CUSIP']}
                    </Typography>
                  </Box>
                )}
              </Stack>

              {/* Action Buttons */}
              <Stack spacing={1.5} sx={{ mt: 3 }}>
                {/* Load button - full width */}
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PlayArrowIcon sx={{ fontSize: 20 }} />}
                  onClick={() => onLoad(deal)}
                  fullWidth
                  sx={{
                    py: 2,
                    fontSize: '16px',
                    textTransform: 'none',
                    fontWeight: 700,
                    minHeight: 56,
                    borderRadius: 2,
                    boxShadow: 3
                  }}
                >
                  Load Deal
                </Button>
                
                {/* Edit and Clone buttons - side by side */}
                <Stack direction="row" spacing={1.5}>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon sx={{ fontSize: 18 }} />}
                    onClick={() => onEdit(deal)}
                    fullWidth
                    sx={{
                      py: 2,
                      fontSize: '15px',
                      textTransform: 'none',
                      minHeight: 56,
                      borderRadius: 2,
                      fontWeight: 600,
                      borderWidth: 2,
                      '&:hover': {
                        borderWidth: 2
                      }
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ContentCopyIcon sx={{ fontSize: 18 }} />}
                    onClick={() => onClone(deal)}
                    fullWidth
                    sx={{
                      py: 2,
                      fontSize: '15px',
                      textTransform: 'none',
                      minHeight: 56,
                      borderRadius: 2,
                      fontWeight: 600,
                      borderWidth: 2,
                      '&:hover': {
                        borderWidth: 2
                      }
                    }}
                  >
                    Clone
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default MobileDealCards;
